<script setup lang="ts">
import Debug from 'debug'
import { SCREENS_FIELDS, SCREENS_TITLES } from '~/lib/models/data-onboarding'
import { ScreensType } from '~/lib/services/middleware/middleware.dtos'
import { EnrollmentScreensData } from '~/lib/services/store/auth.dto'

const debug = Debug(`app:component:DataOnboarding`)

const props = defineProps<{ screens: Array<EnrollmentScreensData> }>()
const emit = defineEmits(['closeModal', 'succededEnroll'])

const { t } = useLocale()
const authStore = useAuthStore()
const { documentValidationTypes, hasLoyaltyActive, hasAutoLoyaltyActive } = useAppStore()
const { trackOnCloseModal } = useGTM()
const { documentTypeValues } = useEnrollmentDynamicFields()

const documentTypes = computed(() => {
  if (!documentValidationTypes) return []
  return documentValidationTypes.filter(documentType =>
    documentTypeValues.value.includes(documentType.name.toLowerCase())
  )
})

const selectedScreen = ref<ScreensType>({
  index: 0,
  title: '',
  fields: [''],
  active: false,
  completed: false,
})

const onClose = () => {
  emit('closeModal')
  trackOnCloseModal()
}

const next = () => {
  props.screens.forEach(screen => {
    if (screen.index === selectedScreen.value.index) {
      screen.active = false
      screen.completed = true
    } else if (screen.index === selectedScreen.value.index + 1) {
      screen.active = true
    }
  })
  // detect current selectedScreen is last index on screens
  const hasNextIndex = !!props.screens.find(({ index }) => index === selectedScreen.value.index + 1)

  if (!hasNextIndex) {
    completeProccess()
  } else {
    selectedScreen.value =
      props.screens.find(({ index }) => index === selectedScreen.value.index + 1) ??
      selectedScreen.value
  }
}

const back = () => {
  props.screens.forEach(screen => {
    if (screen.index === selectedScreen.value.index) {
      screen.active = false
    } else if (screen.index === selectedScreen.value.index - 1) {
      screen.active = true
    }
  })
  selectedScreen.value =
    props.screens.find(({ index }) => index === selectedScreen.value.index - 1) ??
    selectedScreen.value
}

const saveDataOnboarding = (newData = {}) => {
  const savedData = JSON.parse(
    window.localStorage.getItem(`${authStore.userMcId}-data-onboarding`) ?? '{}'
  )
  Object.assign(savedData, newData)
  window.localStorage.setItem(`${authStore.userMcId}-data-onboarding`, JSON.stringify(savedData))
}

const showBirthdateForm = computed(
  () =>
    selectedScreen.value &&
    selectedScreen.value.title === SCREENS_TITLES.BIRTHDATE &&
    selectedScreen.value.fields.includes(SCREENS_FIELDS.BIRTHDATE_STR)
)

const showPhoneForm = computed(
  () =>
    selectedScreen.value &&
    selectedScreen.value.title === SCREENS_TITLES.PHONE &&
    selectedScreen.value.fields.includes(SCREENS_FIELDS.PHONE)
)

const firstSelectedScreen = computed(() => selectedScreen.value.index === 1)

const completeProccess = async () => {
  try {
    if (hasLoyaltyActive && hasAutoLoyaltyActive && authStore.hasRequiredLoyaltyData) {
      await authStore.registerUserInLoyalty()
    }
    window.localStorage.removeItem(`${authStore.userMcId}-data-onboarding`)
    emit('succededEnroll')
  } catch (error) {
    debug('🚀 ~ file: LegacyPaymentIframe.vue:106 ~ completeProccess ~ error:', error)
    window.localStorage.setItem(`${authStore.userMcId}-pending-registration`, 'true') // after fail 3 times to register user in Loyalty we save user data to add them when we could connect to im
  } finally {
    emit('closeModal')
  }
}

onBeforeMount(async () => {
  selectedScreen.value = props.screens[0]
})
</script>

<template>
  <main data-test="register-data-onboarding">
    <ModalCenter
      :content-full="true"
      :add-close-button="false"
      :desktop-size="'small'"
      @close="onClose"
    >
      <template #header>
        <section v-if="selectedScreen" class="flex flex-col gap-4 px-4 md:px-0">
          <CloseIcon v-if="firstSelectedScreen" class="w-4" @click="onClose" />
          <img
            v-else
            class="h-6 w-6 cursor-pointer"
            src="/images/icons/arrowLeft.svg"
            alt="arrowLeft"
            @click="back"
          />
          <BadgeStepper
            :screens="props.screens"
            :current-index="selectedScreen.index"
            class="mt-4"
          />
          <h3 class="text-3xl font-bold">{{ t(`enrollment.${selectedScreen.title}.title`) }}</h3>
          <p class="text-sm font-normal">{{ t(`enrollment.${selectedScreen.title}.body`) }}</p>
        </section>
      </template>
      <template #content>
        <section class="mt-4 h-full">
          <LazyRegisterBirthdateForm
            v-if="showBirthdateForm"
            @save-data="saveDataOnboarding"
            @next="next"
          />
          <LazyRegisterNameForm
            v-else-if="selectedScreen.title === SCREENS_TITLES.NAME"
            :fields="selectedScreen.fields"
            @save-data="saveDataOnboarding"
            @next="next"
          />
          <LazyRegisterDocumentForm
            v-else-if="selectedScreen.title === SCREENS_TITLES.DOCUMENT"
            :document-types="documentTypes"
            :fields="selectedScreen.fields"
            @save-data="saveDataOnboarding"
            @next="next"
          />
          <LazyRegisterPhoneForm v-else-if="showPhoneForm" @complete-proccess="next" />
        </section>
      </template>
    </ModalCenter>
  </main>
</template>
